import { create } from 'zustand';
import {
  getAdminChangelog, getAdminChangelogEntry, createChangelogEntry, updateChangelogEntry,
  publishChangelogEntry, unpublishChangelogEntry, deleteChangelogEntry,
  getAdminFaqs, createFaq, updateFaq, deleteFaq,
  getAdminArticles, getAdminArticle, createArticle, updateArticle, deleteArticle,
  ChangelogEntry, Faq, Article, Pagination, ChangelogItem,
} from '@/services/PagesServices/KnowledgeBaseService';

interface KnowledgeBaseState {
  changelog: ChangelogEntry[];
  selectedEntry: ChangelogEntry | null;
  faqs: Faq[];
  articles: Article[];
  pagination: Pagination | null;
  articlesPagination: Pagination | null;
  loading: boolean;
  loadingFaqs: boolean;
  loadingArticles: boolean;
  saving: boolean;
  error: string | null;

  fetchChangelog: (page?: number, search?: string, status?: string) => Promise<void>;
  fetchChangelogEntry: (uuid: string) => Promise<ChangelogEntry | null>;
  createEntry: (data: { version: string; title: string; description?: string; published_date: string; status?: string; audience_roles?: string; items?: Partial<ChangelogItem>[] }) => Promise<{ success: boolean; message: string }>;
  updateEntry: (uuid: string, data: any) => Promise<{ success: boolean; message: string }>;
  publishEntry: (uuid: string) => Promise<{ success: boolean; message: string }>;
  unpublishEntry: (uuid: string) => Promise<{ success: boolean; message: string }>;
  deleteEntry: (uuid: string) => Promise<{ success: boolean; message: string }>;
  setSelectedEntry: (entry: ChangelogEntry | null) => void;

  fetchArticles: (page?: number, search?: string, category?: string, status?: string) => Promise<void>;
  fetchArticle: (uuid: string) => Promise<Article | null>;
  createArticleItem: (data: any) => Promise<{ success: boolean; message: string }>;
  updateArticleItem: (uuid: string, data: any) => Promise<{ success: boolean; message: string }>;
  deleteArticleItem: (uuid: string) => Promise<{ success: boolean; message: string }>;

  fetchFaqs: (search?: string, category?: string) => Promise<void>;
  createFaqItem: (data: { question: string; answer: string; category?: string; sort_order?: number; is_active?: boolean; audience_roles?: string }) => Promise<{ success: boolean; message: string }>;
  updateFaqItem: (uuid: string, data: any) => Promise<{ success: boolean; message: string }>;
  deleteFaqItem: (uuid: string) => Promise<{ success: boolean; message: string }>;
}

const useKnowledgeBaseStore = create<KnowledgeBaseState>((set, get) => ({
  changelog: [],
  selectedEntry: null,
  faqs: [],
  articles: [],
  pagination: null,
  articlesPagination: null,
  loading: false,
  loadingFaqs: false,
  loadingArticles: false,
  saving: false,
  error: null,

  fetchChangelog: async (page = 1, search = '', status = '') => {
    set({ loading: true, error: null });
    try {
      const res = await getAdminChangelog(page, 20, search, status);
      if (!res.success) throw new Error(res.error?.message);
      set({ changelog: res.data || [], pagination: res.pagination || null, loading: false });
    } catch (err: any) { set({ error: err.message, loading: false }); }
  },

  fetchChangelogEntry: async (uuid) => {
    try {
      const res = await getAdminChangelogEntry(uuid);
      if (!res.success) throw new Error(res.error?.message);
      set({ selectedEntry: res.data || null });
      return res.data || null;
    } catch { return null; }
  },

  createEntry: async (data) => {
    set({ saving: true });
    try {
      const res = await createChangelogEntry(data);
      if (!res.success) throw new Error(res.error?.message);
      set({ saving: false });
      get().fetchChangelog();
      return { success: true, message: res.message || 'Change log entry created' };
    } catch (err: any) { set({ saving: false }); return { success: false, message: err.message }; }
  },

  updateEntry: async (uuid, data) => {
    set({ saving: true });
    try {
      const res = await updateChangelogEntry(uuid, data);
      if (!res.success) throw new Error(res.error?.message);
      set({ saving: false, selectedEntry: res.data || null });
      get().fetchChangelog();
      return { success: true, message: 'Change log entry updated' };
    } catch (err: any) { set({ saving: false }); return { success: false, message: err.message }; }
  },

  publishEntry: async (uuid) => {
    try {
      const res = await publishChangelogEntry(uuid);
      if (!res.success) throw new Error(res.error?.message);
      get().fetchChangelog();
      return { success: true, message: res.message || 'Published' };
    } catch (err: any) { return { success: false, message: err.message }; }
  },

  unpublishEntry: async (uuid) => {
    try {
      const res = await unpublishChangelogEntry(uuid);
      if (!res.success) throw new Error(res.error?.message);
      get().fetchChangelog();
      return { success: true, message: res.message || 'Unpublished' };
    } catch (err: any) { return { success: false, message: err.message }; }
  },

  deleteEntry: async (uuid) => {
    try {
      const res = await deleteChangelogEntry(uuid);
      if (!res.success) throw new Error(res.error?.message);
      get().fetchChangelog();
      return { success: true, message: res.message || 'Deleted' };
    } catch (err: any) { return { success: false, message: err.message }; }
  },

  setSelectedEntry: (entry) => set({ selectedEntry: entry }),

  fetchFaqs: async (search = '', category = '') => {
    set({ loadingFaqs: true });
    try {
      const res = await getAdminFaqs(search, category);
      if (!res.success) throw new Error(res.error?.message);
      set({ faqs: res.data || [], loadingFaqs: false });
    } catch (err: any) { set({ loadingFaqs: false }); }
  },

  createFaqItem: async (data) => {
    set({ saving: true });
    try {
      const res = await createFaq(data);
      if (!res.success) throw new Error(res.error?.message);
      set({ saving: false });
      get().fetchFaqs();
      return { success: true, message: 'FAQ created' };
    } catch (err: any) { set({ saving: false }); return { success: false, message: err.message }; }
  },

  updateFaqItem: async (uuid, data) => {
    set({ saving: true });
    try {
      const res = await updateFaq(uuid, data);
      if (!res.success) throw new Error(res.error?.message);
      set({ saving: false });
      get().fetchFaqs();
      return { success: true, message: 'FAQ updated' };
    } catch (err: any) { set({ saving: false }); return { success: false, message: err.message }; }
  },

  deleteFaqItem: async (uuid) => {
    try {
      const res = await deleteFaq(uuid);
      if (!res.success) throw new Error(res.error?.message);
      get().fetchFaqs();
      return { success: true, message: res.message || 'Deleted' };
    } catch (err: any) { return { success: false, message: err.message }; }
  },

  fetchArticles: async (page = 1, search = '', category = '', status = '') => {
    set({ loadingArticles: true });
    try {
      const res = await getAdminArticles(page, 20, search, category, status);
      if (!res.success) throw new Error(res.error?.message);
      set({ articles: res.data || [], articlesPagination: res.pagination || null, loadingArticles: false });
    } catch (err: any) { set({ loadingArticles: false }); }
  },

  fetchArticle: async (uuid) => {
    try {
      const res = await getAdminArticle(uuid);
      if (!res.success) throw new Error(res.error?.message);
      return res.data || null;
    } catch { return null; }
  },

  createArticleItem: async (data) => {
    set({ saving: true });
    try {
      const res = await createArticle(data);
      if (!res.success) throw new Error(res.error?.message);
      set({ saving: false });
      get().fetchArticles();
      return { success: true, message: 'Article created' };
    } catch (err: any) { set({ saving: false }); return { success: false, message: err.message }; }
  },

  updateArticleItem: async (uuid, data) => {
    set({ saving: true });
    try {
      const res = await updateArticle(uuid, data);
      if (!res.success) throw new Error(res.error?.message);
      set({ saving: false });
      get().fetchArticles();
      return { success: true, message: 'Article updated' };
    } catch (err: any) { set({ saving: false }); return { success: false, message: err.message }; }
  },

  deleteArticleItem: async (uuid) => {
    try {
      const res = await deleteArticle(uuid);
      if (!res.success) throw new Error(res.error?.message);
      get().fetchArticles();
      return { success: true, message: res.message || 'Deleted' };
    } catch (err: any) { return { success: false, message: err.message }; }
  },
}));

export default useKnowledgeBaseStore;
